/**
 * UI-3c Task 7 — callbacks Zustand store.
 *
 * Holds the current agent's PENDING callbacks. `useMyCallbacks` fetches and
 * writes the list here; socket handlers (`callback:scheduled` / `:originated`
 * / `:failed`) patch it incrementally. Consumers subscribe to `myCallbacks`
 * so both poll-driven and socket-driven updates reach the UI.
 */
import { create } from 'zustand'
import type { Callback } from '../components/admin/callbacks/CallbackDrawer.js'

interface CallbacksStoreState {
  myCallbacks: Callback[]
  setMyCallbacks: (callbacks: Callback[]) => void
  /** Prepend a callback. Idempotent — a callback already in the list is not double-added. */
  prependCallback: (callback: Callback) => void
  removeCallback: (id: string) => void
  patchCallback: (id: string, patch: Partial<Callback>) => void
  reset: () => void
}

export const useCallbacksStore = create<CallbacksStoreState>()((set) => ({
  myCallbacks: [],
  setMyCallbacks: (myCallbacks) => set({ myCallbacks }),
  prependCallback: (callback) =>
    set((state) =>
      state.myCallbacks.some((c) => c.id === callback.id)
        ? state
        : { myCallbacks: [callback, ...state.myCallbacks] },
    ),
  removeCallback: (id) =>
    set((state) => ({
      myCallbacks: state.myCallbacks.filter((c) => c.id !== id),
    })),
  patchCallback: (id, patch) =>
    set((state) => ({
      myCallbacks: state.myCallbacks.map((c) =>
        c.id === id ? { ...c, ...patch } : c,
      ),
    })),
  reset: () => set({ myCallbacks: [] }),
}))
